import { Injectable, NgModule } from '@angular/core'
import { RouterModule, RouterStateSnapshot, Routes, TitleStrategy } from '@angular/router'
import { CurrentOrganizationGuard } from './shared/services/current-organization/current-organization.guard'
import { CurrentUserGuard } from './shared/services/current-user'
import { ErrorPage } from '@shared/pages/error-page'
import { ErrorPagesGuard } from './shared/pages/error-page/error-pages.guard'
import { Title } from '@angular/platform-browser'
import { TranslateService } from './shared/services/translate'
import { SupportPage } from './shared/pages/support-page'
import { TeamModeGuard } from './shared/guards/team-mode.guard'

@Injectable({ providedIn: 'root' })
export class TemplatePageTitleStrategy extends TitleStrategy {
  constructor(
    private readonly title: Title,
    private translate: TranslateService
  ) {
    super()
  }
  locales = { en: { title: 'Glabix' }, ru: { title: 'Глабикс' } }
  t = this.translate.create(this.locales)

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState)
    if (title !== undefined) {
      this.title.setTitle(title)
    } else {
      this.title.setTitle(this.t('title'))
    }
  }
}

const routes: Routes = [
  {
    path: 'search',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/search-module/search.module').then((m) => m.SearchModule),
  },
  {
    path: 'org/:organization_id/tasks',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/tasks-module/all-tasks.module').then((m) => m.AllTasksModule),
  },
  {
    path: 'org/:organization_id/projects',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/projects-module/projects.module').then((m) => m.ProjectsModule),
  },
  {
    path: 'org/:organization_id/users',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/users-module/users.module').then((m) => m.UsersModule),
  },
  {
    path: 'org/:organization_id/settings',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/settings-module/settings.module').then((m) => m.SettingsModule),
  },
  {
    path: 'org/:organization_id/stream',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard, TeamModeGuard],
    loadChildren: () => import('@app/stream-module/stream.module').then((m) => m.StreamModule),
  },
  {
    path: 'org/:organization_id/daily',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/daily-module/daily.module').then((m) => m.DailyModule),
  },

  {
    path: 'navigation',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/navigation-module/navigation.module').then((m) => m.NavigationModule),
  },

  {
    path: 'reports',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/reports-module/reports.module').then((m) => m.ReportsModule),
  },
  {
    path: ':organization_id/reports',
    canActivate: [CurrentUserGuard],
    loadChildren: () =>
      import('@app/reports-module/organization-reports.module').then((m) => m.OrganizationReportsModule),
  },

  {
    path: 'my/files',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/my-files-module/my-files.module').then((m) => m.MyFilesModule),
  },

  {
    path: 'my/calendar',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/my-calendar-module/my-calendar.module').then((m) => m.MyCalendarModule),
  },

  {
    path: 'milestones',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/milestones-module/milestones.module').then((m) => m.MilestonesModule),
  },

  {
    path: 'my/notes',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/my-notes-module/my-notes.module').then((m) => m.MyNotesModule),
  },

  {
    path: 'my/discussions',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/my-discussions-module/my-discussions.module').then((m) => m.MyDiscussionsModule),
  },

  {
    path: 'identities',
    loadChildren: () => import('@app/identities-module/identities.module').then((m) => m.IdentitiesModule),
  },

  {
    path: 'accounts',
    loadChildren: () => import('@app/accounts-module/accounts.module').then((m) => m.AccountsModule),
  },

  {
    path: 'org/:organization_id/onboarding',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/onboarding-module/onboarding.module').then((m) => m.OnboardingModule),
  },
  {
    path: 'org/:organization_id/blocked_no_payment',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    loadChildren: () => import('@app/blocked-pages-module/blocked-pages.module').then((m) => m.BlockedPagesModule),
  },

  {
    path: 'org/:organization_id/support',
    canActivate: [CurrentUserGuard, CurrentOrganizationGuard],
    component: SupportPage,
  },
  {
    path: 'accept_invitation',
    loadChildren: () =>
      import('@app/accept-invitation-module/accept-invitation.module').then((m) => m.AcceptInvitationModule),
  },
  {
    path: 'admin',
    canActivate: [CurrentUserGuard],
    loadChildren: () => import('@app/admin-module/admin.module').then((m) => m.AdminModule),
  },
  {
    path: 'bitrix24/callback',
    canActivate: [],
    loadChildren: () =>
      import('@app/bitrix24-callback-module/bitrix24-callback.module').then((m) => m.Bitrix24CallbackModule),
  },
  {
    path: 'amocrm/callback',
    canActivate: [],
    loadChildren: () =>
      import('@app/amocrm-callback-module/amocrm-callback.module').then((m) => m.AmocrmCallbackModule),
  },
  {
    path: 'recorder',
    canActivate: [],
    loadChildren: () => import('@app/recorder-module/recorder.module').then((m) => m.RecorderModule),
  },
  {
    path: 'share',
    canActivate: [],
    loadChildren: () => import('@app/public-video-module/public-video.module').then((m) => m.PublicVideoModule),
  },
  {
    path: '403',
    canActivate: [ErrorPagesGuard],
    component: ErrorPage,
    data: { code: 403 },
  },
  {
    path: '404',
    canActivate: [ErrorPagesGuard],
    component: ErrorPage,
    data: { code: 404 },
  },
  {
    path: '500',
    canActivate: [ErrorPagesGuard],
    component: ErrorPage,
    data: { code: 500 },
  },
  {
    path: '502',
    canActivate: [ErrorPagesGuard],
    component: ErrorPage,
    data: { code: 502 },
  },
  {
    path: '',
    redirectTo: '/accounts/switch',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '404',
  },
]

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: TemplatePageTitleStrategy }],
})
export class AppRoutingModule {}
